import React, { useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  Task,
  TaskAttachment,
  CreateTaskData,
  UpdateTaskData,
  TaskStatus,
  TaskPriority,
} from '../../types/task.types';
import { User } from '../../types/auth.types';
import { Risk } from '../../types/risk.types';
import taskService from '../../services/task.service';
import authService from '../../services/auth.service';
import riskService from '../../services/risk.service';
import { useCreateTask, useTaskDetail, useUpdateTask } from '../../hooks/useTaskQueries';
import { sanitizeHtml } from '../../lib/sanitizeHtml';
import {
  LedgerCombobox,
  LedgerDialog,
  LedgerField,
  LedgerInput,
  LedgerSelect,
  LedgerSelectOption,
  LedgerWizardStep,
  useLedgerWizard,
} from '../ui';
import TaskAttachmentUpload from './TaskAttachmentUpload';
import TaskAttachmentList from './TaskAttachmentList';

// Optional fields carry .defined().default(...) so the schema's inferred type
// lines up with what yupResolver hands back. Dates live as 'YYYY-MM-DD'
// strings (the native date input's wire format), so the payload mapper can
// pass them straight through instead of round-tripping Date objects.
const schema = yup.object({
  title: yup.string().required('Title is required').default(''),
  description: yup.string().required('Description is required').default(''),
  status: yup.string().required('Status is required').default('TODO'),
  priority: yup.string().required('Priority is required').default('MEDIUM'),
  assignee: yup.string().required('Assignee is required').default(''),
  risk: yup.string().required('Related risk is required').default(''),
  dueDate: yup.string().defined().default(''),
  startDate: yup.string().defined().default(''),
  completedDate: yup.string().defined().default(''),
  estimatedHours: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? null : value))
    .min(0)
    .nullable()
    .defined()
    .default(0),
  actualHours: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? null : value))
    .min(0)
    .nullable()
    .defined()
    .default(0),
  progress: yup.number().min(0).max(100).defined().default(0),
  tags: yup.array().of(yup.string().defined()).defined().default([]),
  notes: yup.string().defined().default(''),
});

type TaskFormValues = yup.InferType<typeof schema>;

const defaultValues: TaskFormValues = {
  title: '',
  description: '',
  status: 'TODO',
  priority: 'MEDIUM',
  assignee: '',
  risk: '',
  dueDate: '',
  startDate: '',
  completedDate: '',
  estimatedHours: 0,
  actualHours: 0,
  progress: 0,
  tags: [],
  notes: '',
};

// The one payload mapper: the exact submit assembly used for creates.
// Updates decorate it with the edit-only fields below.
const toTaskPayload = (values: TaskFormValues): CreateTaskData => ({
  title: values.title,
  description: values.description,
  status: values.status as TaskStatus,
  priority: values.priority as TaskPriority,
  assignee: values.assignee,
  risk: values.risk || undefined,
  dueDate: values.dueDate || undefined,
  startDate: values.startDate || undefined,
  estimatedHours: values.estimatedHours || undefined,
  tags: values.tags,
  notes: values.notes || undefined,
});

const toUpdateTaskPayload = (values: TaskFormValues): UpdateTaskData => ({
  ...toTaskPayload(values),
  completedDate: values.completedDate || undefined,
  actualHours: values.actualHours || undefined,
  progress: values.progress,
});

const statusOptions: LedgerSelectOption[] = [
  { value: 'TODO', label: 'To Do' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'REVIEW', label: 'In Review' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const priorityOptions: LedgerSelectOption[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
];

// Quill toolbar configuration. Module scope keeps its identity stable —
// Quill re-registers modules when it changes.
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean'],
  ],
};

const sectionHeadClass = 'cap-label mb-3 mt-1 border-b border-ink pb-0.5 sm:col-span-6';

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-rule-faint py-1.5">
    <span className="cap-label shrink-0">{label}</span>
    <span className="text-right text-scale-1 text-ink">{children}</span>
  </div>
);

interface TaskFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  task?: Task | null;
}

const TaskFormContent: React.FC<TaskFormDialogProps> = ({ open, onClose, onSuccess, task }) => {
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [risks, setRisks] = useState<Risk[]>([]);
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [newTag, setNewTag] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const saving = createTask.isPending || updateTask.isPending;

  // The list endpoint omits description, notes, tags, both hour fields and the
  // secondary dates, and TasksPage hands this dialog a list row. Seeding from
  // that row alone left seven fields blank on every edit, and saving wrote the
  // blanks back. Read the detail record, exactly as TaskPeek already does.
  // Opening the peek first warms the same query, so this is usually a cache hit.
  const detail = useTaskDetail(open && task ? task.id : null);
  const subject = detail.data ?? task;

  // Progress comes from the checklist whenever there is one; the backend
  // re-derives it on save and refuses a manual write, so the slider would be
  // offering a value it cannot keep.
  const checklistItems = subject && 'checklist_items' in subject ? subject.checklist_items : undefined;
  const derivedProgress = (checklistItems?.length ?? 0) > 0;

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useFormContext<TaskFormValues>();

  const riskValue = watch('risk');
  const tags = watch('tags');
  const progressValue = watch('progress');
  const notesValue = watch('notes');

  // Every async loader takes an isStale probe instead of setting state
  // blindly: StrictMode double-fires these effects, and a late response from
  // a torn-down run must not clobber what the newer run (or the user) set.
  const loadRisks = async (isStale: () => boolean) => {
    try {
      const risksData = await riskService.getRisks();
      if (!isStale()) setRisks(risksData);
    } catch (error) {
      console.error('Failed to load risks:', error);
    }
  };

  useEffect(() => {
    if (!open) return;
    let ignore = false;
    loadRisks(() => ignore);
    return () => {
      ignore = true;
    };
  }, [open]);

  // A task's assignee must be able to see its linked risk, which the backend
  // scopes to the risk's company. Load the assignee candidates scoped to the
  // selected risk (empty until one is chosen), and drop an assignee stranded
  // by a risk switch that moves the task to a different organization.
  useEffect(() => {
    if (!open) return;
    let ignore = false;
    if (!riskValue) {
      setUsers([]);
      return () => {
        ignore = true;
      };
    }
    authService
      .getUsers({ assignableToRisk: riskValue, isActive: true })
      .then((scoped) => {
        if (ignore) return;
        setUsers(scoped);
        const current = watch('assignee');
        if (current && !scoped.some((candidate) => candidate.id === current)) {
          setValue('assignee', '');
        }
      })
      .catch((error) => {
        console.error('Failed to load assignable users:', error);
        if (!ignore) setUsers([]);
      });
    return () => {
      ignore = true;
    };
  }, [open, riskValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadAttachments = async () => {
    if (!task) return;
    // Reload the task so the list reflects what the server actually stored.
    const updatedTask = await taskService.getTask(task.id);
    setAttachments(updatedTask.attachments || []);
  };

  // Until a risk is chosen the assignable set is unknown, so prompt for the
  // risk instead of offering an empty picker.
  const assigneePlaceholder = riskValue ? 'Select an assignee' : 'Select a risk first';
  const userOptions: LedgerSelectOption[] = [
    { value: '', label: assigneePlaceholder },
    ...users.map((candidate) => ({
      value: candidate.id,
      label: candidate.fullName || candidate.email,
    })),
  ];
  const riskOptions: LedgerSelectOption[] = [
    { value: '', label: 'Select a risk' },
    ...risks.map((risk) => ({ value: risk.id, label: risk.title })),
  ];

  const addTag = () => {
    const tag = newTag.trim();
    if (!tag) return;
    setValue('tags', [...tags, tag], { shouldDirty: true });
    setNewTag('');
  };
  const removeTag = (index: number) => {
    setValue('tags', tags.filter((_, i) => i !== index), { shouldDirty: true });
  };

  const titleField = register('title');

  const steps: LedgerWizardStep<TaskFormValues>[] = [
    {
      key: 'details',
      label: 'Details',
      fields: [
        'title', 'description', 'status', 'priority', 'risk', 'assignee',
        'dueDate', 'startDate', 'completedDate', 'estimatedHours', 'actualHours',
        'progress', 'tags',
      ],
      content: (
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-6">
          <LedgerField
            label="Task Title"
            htmlFor="task-title"
            error={errors.title?.message}
            className="sm:col-span-6"
          >
            <LedgerInput
              id="task-title"
              required
              invalid={!!errors.title}
              {...titleField}
              ref={(element) => {
                titleField.ref(element);
                titleInputRef.current = element;
              }}
            />
          </LedgerField>

          <LedgerField
            label="Description"
            htmlFor="task-description"
            error={errors.description?.message}
            className="sm:col-span-6"
          >
            <textarea
              id="task-description"
              rows={3}
              required
              aria-invalid={errors.description ? true : undefined}
              className="w-full border border-rule-faint bg-transparent p-2 text-scale-1 text-ink outline-none focus:border-accent"
              {...register('description')}
            />
          </LedgerField>

          <LedgerField label="Status" error={errors.status?.message} className="sm:col-span-3">
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <LedgerSelect
                  ariaLabel="Status"
                  value={field.value}
                  options={statusOptions}
                  onValueChange={field.onChange}
                />
              )}
            />
          </LedgerField>

          <LedgerField label="Priority" error={errors.priority?.message} className="sm:col-span-3">
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <LedgerSelect
                  ariaLabel="Priority"
                  value={field.value}
                  options={priorityOptions}
                  onValueChange={field.onChange}
                />
              )}
            />
          </LedgerField>

          <div className={sectionHeadClass}>Assignment</div>

          <LedgerField label="Related Risk" error={errors.risk?.message} className="sm:col-span-3">
            <Controller
              name="risk"
              control={control}
              render={({ field }) => (
                <LedgerCombobox
                  ariaLabel="Related Risk"
                  searchLabel={`Search ${Math.max(0, riskOptions.length - 1)} risks`}
                  value={field.value}
                  options={riskOptions}
                  onValueChange={field.onChange}
                />
              )}
            />
          </LedgerField>

          <LedgerField label="Assignee" error={errors.assignee?.message} className="sm:col-span-3">
            <Controller
              name="assignee"
              control={control}
              render={({ field }) => (
                <LedgerCombobox
                  ariaLabel="Assignee"
                  searchLabel="Search people"
                  value={field.value}
                  options={userOptions}
                  onValueChange={field.onChange}
                />
              )}
            />
          </LedgerField>

          <div className={sectionHeadClass}>Timeline</div>

          <LedgerField label="Start Date" htmlFor="task-start-date" className="sm:col-span-2">
            <LedgerInput id="task-start-date" type="date" {...register('startDate')} />
          </LedgerField>

          <LedgerField label="Due Date" htmlFor="task-due-date" className="sm:col-span-2">
            <LedgerInput id="task-due-date" type="date" {...register('dueDate')} />
          </LedgerField>

          <LedgerField
            label="Estimated Hours"
            htmlFor="task-estimated-hours"
            error={errors.estimatedHours?.message}
            className="sm:col-span-2"
          >
            <LedgerInput
              id="task-estimated-hours"
              type="number"
              invalid={!!errors.estimatedHours}
              {...register('estimatedHours', { valueAsNumber: true })}
            />
          </LedgerField>

          {task && (
            <>
              <LedgerField
                label="Actual Hours"
                htmlFor="task-actual-hours"
                error={errors.actualHours?.message}
                className="sm:col-span-3"
              >
                <LedgerInput
                  id="task-actual-hours"
                  type="number"
                  invalid={!!errors.actualHours}
                  {...register('actualHours', { valueAsNumber: true })}
                />
              </LedgerField>

              <LedgerField label="Completed Date" htmlFor="task-completed-date" className="sm:col-span-3">
                <LedgerInput id="task-completed-date" type="date" {...register('completedDate')} />
              </LedgerField>

              <LedgerField
                label={`Progress: ${progressValue}%`}
                htmlFor="task-progress"
                className="sm:col-span-6"
                hint={
                  derivedProgress
                    ? `Counted from the checklist, ${checklistItems?.filter((item) => item.is_complete).length ?? 0} of ${checklistItems?.length ?? 0} done`
                    : undefined
                }
              >
                <Controller
                  name="progress"
                  control={control}
                  render={({ field }) => (
                    <input
                      id="task-progress"
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={field.value}
                      disabled={derivedProgress}
                      onChange={(event) => field.onChange(Number(event.target.value))}
                      className="w-full accent-accent disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  )}
                />
              </LedgerField>
            </>
          )}

          <LedgerField label="Tags" htmlFor="task-tags" hint="Press Enter to add tags" className="sm:col-span-6">
            <>
              {tags.length > 0 && (
                <div className="mb-1.5 flex flex-wrap gap-1.5">
                  {tags.map((tag, index) => (
                    <span
                      // Duplicates are allowed (as before), so the key needs the index.
                      key={`${tag}-${index}`}
                      className="flex items-center gap-1 border border-ink px-1.5 py-0.5 text-scale-0 text-ink"
                    >
                      {tag}
                      <button
                        type="button"
                        aria-label={`Remove tag ${tag}`}
                        onClick={() => removeTag(index)}
                        className="cursor-pointer text-ink-muted hover:text-ink"
                      >
                        &#x2715;
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <LedgerInput
                id="task-tags"
                placeholder="Add tags..."
                value={newTag}
                onChange={(event) => setNewTag(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  // Enter files the tag; without this it would submit the form.
                  event.preventDefault();
                  addTag();
                }}
              />
            </>
          </LedgerField>
        </div>
      ),
    },
    {
      key: 'notes',
      label: 'Notes',
      fields: ['notes'],
      content: (
        <div>
          <div className="cap-label mb-1.5">Notes</div>
          <div className="ledger-quill">
            <ReactQuill
              theme="snow"
              value={notesValue}
              onChange={(value) => setValue('notes', value, { shouldDirty: true })}
              modules={quillModules}
              placeholder="Add detailed notes about this task..."
            />
          </div>
          {!task && (
            <p className="mt-3 text-scale-1 text-ink-muted">
              Attachments can be added after creating the task.
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'attachments',
      label: 'Attachments',
      editOnly: true,
      content: task ? (
        <div>
          <TaskAttachmentUpload taskId={task.id} onUploadComplete={reloadAttachments} />
          <div className="mt-4">
            <TaskAttachmentList
              taskId={task.id}
              attachments={attachments}
              canDelete
              onAttachmentDeleted={reloadAttachments}
            />
          </div>
        </div>
      ) : null,
    },
    {
      key: 'activity',
      label: 'Activity',
      editOnly: true,
      content: task ? (
        <div>
          <div className="cap-label border-b border-ink pb-0.5">Task Information</div>
          <InfoRow label="Reference Number">{task.reference_number || 'Not assigned'}</InfoRow>
          <InfoRow label="Current Status">
            <span className="cap-label border border-ink px-1 !text-ink">{task.status}</span>
          </InfoRow>
          <InfoRow label="Created By">{task.createdByName || task.createdByEmail || 'Unknown'}</InfoRow>
          <InfoRow label="Created At">
            {task.createdAt ? new Date(task.createdAt).toLocaleString() : 'Unknown'}
          </InfoRow>
          <InfoRow label="Last Updated">
            {task.updatedAt ? new Date(task.updatedAt).toLocaleString() : 'Unknown'}
          </InfoRow>
          {task.completedDate && (
            <InfoRow label="Completed On">{new Date(task.completedDate).toLocaleDateString()}</InfoRow>
          )}

          <div className="cap-label mt-5 border-b border-ink pb-0.5">Progress Information</div>
          <div className="flex items-center gap-2 border-b border-rule-faint py-2">
            <span className="cap-label shrink-0">Progress</span>
            <div className="h-1.5 flex-1 bg-rule-faint">
              <div className="h-1.5 bg-accent" style={{ width: `${task.progress}%` }} />
            </div>
            <span className="font-mono text-scale-0 text-ink">{task.progress}%</span>
          </div>
          {(task.estimatedHours || task.actualHours) ? (
            <>
              <InfoRow label="Estimated Hours">{task.estimatedHours || 0} hours</InfoRow>
              <InfoRow label="Actual Hours">{task.actualHours || 0} hours</InfoRow>
            </>
          ) : null}

          {task.riskTitle && (
            <>
              <div className="cap-label mt-5 border-b border-ink pb-0.5">Related Risk</div>
              <p className="py-1.5 text-scale-1 text-ink">{task.riskTitle}</p>
            </>
          )}

          {task.tags && task.tags.length > 0 && (
            <>
              <div className="cap-label mt-5 border-b border-ink pb-0.5">Tags</div>
              <div className="flex flex-wrap gap-1.5 py-1.5">
                {task.tags.map((tag, index) => (
                  <span key={index} className="border border-ink px-1.5 py-0.5 text-scale-0 text-ink">
                    {tag}
                  </span>
                ))}
              </div>
            </>
          )}

          {task.notes && (
            <>
              <div className="cap-label mt-5 border-b border-ink pb-0.5">Task Notes Preview</div>
              <div
                data-testid="task-notes-preview"
                className="ledger-doc text-scale-1 py-1.5"
                // Stored Quill HTML — must pass through sanitizeHtml before any innerHTML render.
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(task.notes) }}
              />
            </>
          )}
        </div>
      ) : null,
    },
  ];

  const {
    rail,
    body,
    footer,
    reset: resetWizard,
    goToFirstInvalid,
  } = useLedgerWizard<TaskFormValues>({
    steps,
    mode: task ? 'free' : 'linear',
    formId: 'task-form',
    submitLabel: task ? 'Save task' : 'Create task',
    isSubmitting: saving || isSubmitting,
    onCancel: () => handleClose(),
  });

  // Reseed whenever the dialog opens or its subject changes so a stale edit
  // subject or error strip never leaks into the next open.
  useEffect(() => {
    setError(null);
    if (subject) {
      const task = subject;
      reset({
        title: task.title,
        description: task.description,
        status: task.status,
        priority: task.priority,
        assignee: task.assignee,
        risk: task.risk || '',
        dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
        startDate: task.startDate ? task.startDate.split('T')[0] : '',
        completedDate: task.completedDate ? task.completedDate.split('T')[0] : '',
        estimatedHours: task.estimatedHours || 0,
        actualHours: task.actualHours || 0,
        progress: task.progress,
        tags: task.tags || [],
        notes: task.notes || '',
      });
      setAttachments(task.attachments || []);
    } else {
      reset(defaultValues);
      setAttachments([]);
    }
    setNewTag('');
    // The dialog owner stays permanently mounted, so the wizard's step state
    // must be rewound explicitly or a reopened dialog resumes mid-flight.
    resetWizard();
  }, [subject, open, reset]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    reset(defaultValues);
    setError(null);
    setAttachments([]);
    setNewTag('');
    resetWizard();
    onClose();
  };

  const onValid = async (values: TaskFormValues) => {
    setError(null);
    try {
      if (task) {
        await updateTask.mutateAsync({ id: task.id, data: toUpdateTaskPayload(values) });
      } else {
        await createTask.mutateAsync(toTaskPayload(values));
      }
      onSuccess();
      handleClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail || 'Failed to save task');
    }
  };

  return (
    <LedgerDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title={task ? `Edit Task: ${task.title}` : 'Create New Task'}
      size="md"
      preventClose={saving || isSubmitting}
      initialFocus={titleInputRef}
      footer={footer}
    >
      <form id="task-form" onSubmit={handleSubmit(onValid, (errors) => goToFirstInvalid(errors))} noValidate>
        {error && (
          <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
            {error}
          </p>
        )}
        {rail}
        {body}
      </form>
    </LedgerDialog>
  );
};

export const TaskFormDialog: React.FC<TaskFormDialogProps> = (props) => {
  const methods = useForm<TaskFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onTouched',
  });
  // The wizard reads trigger/errors through the form context, so the provider
  // wraps the whole dialog body.
  return (
    <FormProvider {...methods}>
      <TaskFormContent {...props} />
    </FormProvider>
  );
};
